<template>
  <component
    :is="getComponent()"
    :to="newTo"
    :target="state.to ? state.target : null"
    class="button"
    :class="[
      state.buttonVariant,
      state.buttonType,
      state.icon && state.iconPosition,
      state.outline,
    ]"
    :aria-label="state.label"
    :role="state.ariaRole"
    @click="handleClick"
    @click.middle="handleClick"
  >
    <template v-if="state.icon">
      <Icon :name="state.icon" class="button-icon" role="presentation" />
    </template>
    <template v-if="!state.iconOnly">
      <span class="button-label">
        {{ state.label }}
      </span>
    </template>
  </component>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components';
import { reactive } from 'vue';
import type {
  IAnalytics,
  TButton,
  TButtonIconPosition,
  TButtonVariant,
  TTarget,
} from '~~/types/local_types';
import useLoadMoreNuxtLink from '~/composables/useLoadMoreNuxtLink';

const props = defineProps({
  to: {
    type: String,
    default: null,
  },
  target: {
    type: String as () => TTarget,
    default: '_self',
  },
  label: {
    type: String,
    default: null,
  },
  type: {
    type: String as () => TButton,
    default: 'default',
  },
  variant: {
    type: String as () => TButtonVariant,
    default: 'primary',
  },
  outline: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: null,
  },
  iconPosition: {
    type: String as () => TButtonIconPosition,
    default: 'left',
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  analytics: {
    type: Object as () => IAnalytics,
    default: null,
  },
  isFauxButton: {
    type: Boolean,
    default: false,
  },
});

const state = reactive({
  to: props.to,
  target: props.target,
  buttonType: `is--${props.type}`,
  buttonVariant: `is--${props.variant}`,
  outline: props.outline && `is--outline`,
  label: props.label,
  icon: props.icon,
  iconOnly: props.type === 'icon',
  iconPosition: `is--${props.iconPosition}-icon`,
  ariaRole: props.to ? 'link' : 'button',
});

const emit = defineEmits(['click']);

const handleClick = (event: MouseEvent) => {
  // don't allow middle click when no link is set
  // or if isFauxButton is set
  if ((!props.to && event.button !== 0) || props.isFauxButton) return;

  const analyticsDefaults = {
    linkText: state.label,
    linkUrl: state.to,
  };
  gtmTrackEvent({ ...analyticsDefaults, ...props.analytics });

  emit('click');
};

const { url: newTo } = useLoadMoreNuxtLink(state.to);

const getComponent = () => {
  if (props.isFauxButton) {
    return 'div';
  }
  if (state.to) {
    return NuxtLink;
  }
  return 'button';
};
</script>

<style lang="postcss" scoped>
.button {
  --button-border-radius: var(--unit-4);
  --button-padding: var(--unit-6) var(--unit-8);
  --button-background: transparent;
  --button-color: hsl(var(--color-neutral-white));
  --button-box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0) inset, 0px 4px 12px -6px rgba(0, 0, 0, 0);
  --button-min-height: 33px;

  outline: none;
  border: none;
  border-radius: var(--button-border-radius);
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-self: center;
  align-items: center;
  position: relative;
  padding: var(--button-padding);
  background: var(--button-background);
  color: var(--button-color);
  cursor: pointer;
  box-shadow: var(--button-box-shadow);
  min-height: var(--button-min-height);
  transition:
    background 250ms ease-in-out,
    color 250ms ease-in-out,
    box-shadow 250ms ease-in-out;

  @media (--lg-viewport) {
    --button-padding: var(--unit-8) var(--unit-12);
  }

  &:after,
  &:before {
    z-index: 1;
    opacity: 0;
    content: '';
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: var(--button-border-radius);
    box-shadow: var(--button-box-shadow);
    background: var(--button-background);
    transition: opacity 250ms ease-in-out;
  }

  &:disabled,
  &.is--disabled {
    opacity: 0.6;
    cursor: default;
    pointer-events: none;
    user-select: none;
  }

  /* VARIANT */
  &.is--cta,
  &.is--primary {
    --button-background: var(--effect-gradient-btn-primary);
    --button-box-shadow: var(--effect-box-shadow-primary);

    &:before {
      --button-background: var(--effect-gradient-btn-primary-hover);
    }
    &:after {
      --button-background: var(--effect-gradient-btn-primary-active);
    }
  }

  &.is--secondary {
    --button-background: var(--effect-gradient-btn-secondary);
    --button-color: hsl(var(--color-primary-700));
    --button-box-shadow: var(--effect-box-shadow-secondary);

    &:before {
      --button-background: var(--effect-gradient-btn-secondary-hover);
      --button-box-shadow: var(--effect-box-shadow-secondary);
    }

    &:after {
      --button-background: var(--effect-gradient-btn-secondary-active);
    }
  }

  /* TYPE */
  &.is--default {
    @add-mixin heading-sm;

    @add-mixin hover {
      box-shadow: initial;

      &:before {
        opacity: 1;
      }
    }

    &:active {
      &:after {
        opacity: 1;
      }
    }
  }

  &.is--label {
    --button-color: hsl(var(--color-primary-700));
    --button-padding: var(--unit-4) var(--unit-8);
    --button-background: hsl(var(--color-accent-light-blue));
    --button-box-shadow: 0;
    @add-mixin label-md-semi-bold;
  }

  &.is--text,
  &.is--text-wcag {
    --button-background: transparent;
    --button-color: hsl(var(--color-secondary-500));
    --button-box-shadow: 0;
    --button-border-radius: 0;
    --button-padding: 0;
    @add-mixin body-md-semi-bold;

    &.is--left-icon {
      .button-label {
        --button-label-padding: 0 0 0 var(--unit-4);
      }
    }

    &.is--right-icon {
      .button-label {
        --button-label-padding: 0 var(--unit-4) 0 0;
      }
    }

    @add-mixin hover {
      --button-color: hsl(var(--color-secondary-300));
    }

    &:active {
      --button-color: hsl(var(--color-secondary-700));
    }
  }

  /*special variant for (grey) info-block WCAG.*/
  &.is--text-wcag {
    --button-color: hsl(var(--color-secondary-700));

    @add-mixin hover {
      --button-color: hsl(var(--color-secondary-500));
    }
  }

  &.is--icon {
    --button-size: var(--unit-30);
    --button-min-height: var(--unit-30);
    --button-padding: var(--unit-6) var(--unit-6);

    height: var(--button-size);
    width: var(--button-size);

    .button-icon {
      --icon-size: var(--unit-18);
      font-size: var(--icon-size);
      flex-shrink: 0;
    }

    @media (--lg-viewport) {
      --button-size: var(--unit-40);
      --button-padding: var(--unit-8) var(--unit-8);
    }

    @add-mixin hover {
      &:before {
        opacity: 1;
      }
    }
    &:active {
      &:after {
        opacity: 1;
      }
    }
  }

  &.is--cta {
    --button-background: var(--effect-gradient-btn-primary);
    --button-padding: var(--unit-8) var(--unit-12);
    --button-color: hsl(var(--color-neutral-white));
    @add-mixin heading-sm;

    .button-label {
      --button-label-padding: 0 var(--unit-6);

      @media (--lg-viewport) {
        --button-label-padding: 0 var(--unit-8);
      }
    }

    @add-mixin hover {
      box-shadow: initial;

      &:before {
        opacity: 1;
      }
    }

    &:active {
      &:after {
        opacity: 1;
      }
    }

    @media (--md-viewport) {
      --button-padding: var(--unit-12) var(--unit-16);
    }
  }

  &.is--outline {
    --button-background: hsl(var(--color-neutral-white));
    --button-box-shadow:
      0 0 0 1px #eaecf6 inset, 0 -1px 0 0 #e3e5f2 inset, 0 2px 6px 0 rgba(229, 231, 246, 0.4);
  }

  /* ICON POSITION */
  &.is--left-icon {
    flex-direction: row;
  }

  &.is--right-icon {
    flex-direction: row-reverse;
  }

  &:focus-visible {
    --button-box-shadow: var(--effect-box-shadow-focus-light) !important;

    &.is--outline {
      &:before {
        opacity: 1;
      }
    }
  }
}

.button-label {
  --button-label-padding: 0 var(--unit-4);

  position: relative;
  z-index: 2;
  padding: var(--button-label-padding);

  @media (--lg-viewport) {
    --button-label-padding: 0 var(--unit-6);
  }
}

.button-icon {
  --icon-size: var(--unit-14);

  position: relative;
  z-index: 2;
  font-size: var(--icon-size);

  @media (--lg-viewport) {
    --icon-size: var(--unit-18);
  }
}
</style>
